import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { logAuditEvent, AuditAction } from './audit';

export type CoreBucket = 'planned' | 'active' | 'done';

export interface ProjectState {
  id?: string;
  label: string;
  coreBucket: CoreBucket;
  order: number;
  locked: boolean;  // prevents deletion of default states
  active: boolean;
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
}

export const DEFAULT_PROJECT_STATES: Omit<ProjectState, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    label: 'Planning',
    coreBucket: 'planned',
    order: 10,
    locked: false, // Allow editing of default state names
    active: true
  },
  {
    label: 'In development',
    coreBucket: 'active',
    order: 20,
    locked: false, // Allow editing of default state names
    active: true
  },
  {
    label: 'Completed',
    coreBucket: 'done',
    order: 30,
    locked: false, // Allow editing of default state names
    active: true
  }
];

// Legacy status to bucket mapping for migration
export const STATUS_TO_BUCKET_MAP: Record<string, CoreBucket> = {
  'planning': 'planned',
  'active': 'active',
  'review': 'active',
  'completed': 'done',
  'archived': 'done'
};

export const STATUS_TO_STATE_MAP: Record<string, string> = {
  'planning': 'planning',
  'active': 'in-development',
  'review': 'in-development',
  'completed': 'completed',
  'archived': 'completed'
};

// Reverse mapping: bucket → legacy status field value
export const BUCKET_TO_STATUS_MAP: Record<CoreBucket, string> = {
  'planned': 'planning',
  'active': 'active',
  'done': 'completed',
};

// Get all project states for an org
export async function getOrgProjectStates(orgId: string): Promise<ProjectState[]> {
  const statesRef = collection(db, 'orgs', orgId, 'projectStates');
  const q = query(statesRef, where('active', '==', true), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ProjectState[];
}

// Get all project states including inactive ones (for admin views)
export async function getAllOrgProjectStates(orgId: string): Promise<ProjectState[]> {
  const statesRef = collection(db, 'orgs', orgId, 'projectStates');
  const q = query(statesRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ProjectState[];
}

// Get a specific project state
export async function getProjectState(orgId: string, stateId: string): Promise<ProjectState | null> {
  const stateRef = doc(db, 'orgs', orgId, 'projectStates', stateId);
  const stateDoc = await getDoc(stateRef);

  if (!stateDoc.exists()) {
    return null;
  }

  return {
    id: stateDoc.id,
    ...stateDoc.data()
  } as ProjectState;
}

// Actor context for audit logging
export interface ActorContext {
  actorId: string;
  actorEmail?: string;
  actorName?: string;
}

// Create a new project state
export async function createProjectState(
  orgId: string,
  stateData: Omit<ProjectState, 'id' | 'createdAt' | 'updatedAt'>,
  actorIdOrContext: string | ActorContext
): Promise<string> {
  const statesRef = collection(db, 'orgs', orgId, 'projectStates');
  const actor = typeof actorIdOrContext === 'string'
    ? { actorId: actorIdOrContext }
    : actorIdOrContext;

  const newState = {
    ...stateData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = await addDoc(statesRef, newState);

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATE_CREATED,
    {
      orgId,
      actorId: actor.actorId,
      actorEmail: actor.actorEmail || '',
      actorName: actor.actorName || '',
    },
    {
      targetId: docRef.id,
      targetType: 'org',
      metadata: {
        label: stateData.label,
        coreBucket: stateData.coreBucket
      },
      description: `Created project state "${stateData.label}" in ${stateData.coreBucket} bucket`
    }
  );

  return docRef.id;
}

// Update a project state
export async function updateProjectState(
  orgId: string,
  stateId: string,
  updates: Partial<Omit<ProjectState, 'id' | 'createdAt'>>,
  actorIdOrContext: string | ActorContext
): Promise<void> {
  const stateRef = doc(db, 'orgs', orgId, 'projectStates', stateId);
  const actor = typeof actorIdOrContext === 'string'
    ? { actorId: actorIdOrContext }
    : actorIdOrContext;

  const updateData = {
    ...updates,
    updatedAt: serverTimestamp()
  };

  await updateDoc(stateRef, updateData);

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATE_UPDATED,
    {
      orgId,
      actorId: actor.actorId,
      actorEmail: actor.actorEmail || '',
      actorName: actor.actorName || '',
    },
    {
      targetId: stateId,
      targetType: 'org',
      metadata: updates,
      description: `Updated project state`
    }
  );
}

// Reorder project states
export async function reorderProjectStates(
  orgId: string,
  stateOrders: { stateId: string; order: number }[],
  actorIdOrContext: string | ActorContext
): Promise<void> {
  const batch = writeBatch(db);
  const actor = typeof actorIdOrContext === 'string'
    ? { actorId: actorIdOrContext }
    : actorIdOrContext;

  for (const { stateId, order } of stateOrders) {
    const stateRef = doc(db, 'orgs', orgId, 'projectStates', stateId);
    batch.update(stateRef, {
      order,
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATES_REORDERED,
    {
      orgId,
      actorId: actor.actorId,
      actorEmail: actor.actorEmail || '',
      actorName: actor.actorName || '',
    },
    {
      targetType: 'org',
      metadata: { newOrder: stateOrders },
      description: `Reordered project states`
    }
  );
}

// Deactivate a project state (soft delete)
export async function deactivateProjectState(
  orgId: string,
  stateId: string,
  actorIdOrContext: string | ActorContext
): Promise<void> {
  const stateRef = doc(db, 'orgs', orgId, 'projectStates', stateId);
  const actor = typeof actorIdOrContext === 'string'
    ? { actorId: actorIdOrContext }
    : actorIdOrContext;

  await updateDoc(stateRef, {
    active: false,
    updatedAt: serverTimestamp()
  });

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATE_DEACTIVATED,
    {
      orgId,
      actorId: actor.actorId,
      actorEmail: actor.actorEmail || '',
      actorName: actor.actorName || '',
    },
    {
      targetId: stateId,
      targetType: 'org',
      description: `Deactivated project state`
    }
  );
}

// Seed default project states for a new org
export async function seedDefaultProjectStates(
  orgId: string,
  actorIdOrContext: string | ActorContext
): Promise<void> {
  // Check if states already exist
  const existingStates = await getOrgProjectStates(orgId);
  if (existingStates.length > 0) {
    return; // Already seeded
  }

  const actor = typeof actorIdOrContext === 'string'
    ? { actorId: actorIdOrContext }
    : actorIdOrContext;

  const batch = writeBatch(db);
  const statesRef = collection(db, 'orgs', orgId, 'projectStates');

  for (const stateData of DEFAULT_PROJECT_STATES) {
    const stateRef = doc(statesRef);
    batch.set(stateRef, {
      ...stateData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }

  await batch.commit();

  // Log audit event
  await logAuditEvent(
    AuditAction.PROJECT_STATES_SEEDED,
    {
      orgId,
      actorId: actor.actorId,
      actorEmail: actor.actorEmail || '',
      actorName: actor.actorName || '',
    },
    {
      targetType: 'org',
      metadata: { statesCount: DEFAULT_PROJECT_STATES.length },
      description: `Seeded ${DEFAULT_PROJECT_STATES.length} default project states`
    }
  );
}

// Get projects grouped by core bucket
export async function getProjectsByBucket(orgId: string): Promise<{
  planned: any[];
  active: any[];
  done: any[];
}> {
  const projectsRef = collection(db, 'orgs', orgId, 'projects');
  const q = query(projectsRef, where('deletedAt', '==', null));
  const snapshot = await getDocs(q);

  const projects = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Array<{id: string, stateCoreBucket?: CoreBucket, [key: string]: any}>;

  const buckets = {
    planned: projects.filter(p => p.stateCoreBucket === 'planned'),
    active: projects.filter(p => p.stateCoreBucket === 'active'),
    done: projects.filter(p => p.stateCoreBucket === 'done')
  };

  return buckets;
}

// Get project counts by bucket for analytics
export async function getBucketCounts(orgId: string): Promise<{
  planned: number;
  active: number;
  done: number;
}> {
  const buckets = await getProjectsByBucket(orgId);

  return {
    planned: buckets.planned.length,
    active: buckets.active.length,
    done: buckets.done.length
  };
}

// Helper to get bucket for a legacy status
export function getCoreBucketForStatus(status: string): CoreBucket {
  return STATUS_TO_BUCKET_MAP[status] || 'active';
}